import { useMemo } from 'react'
import { type AccountData, isEmpty, isNotDefined, type UserId, type Workflow } from '@terros/common'
import { useProfile } from '../../user'
import { getOwnershipWindows, type TimeWindow } from './ownershipWindows'
import { toLegacyActivityAccount } from './legacyActivityAccount'
import { type ActivityAccess, getActivityAccess } from './activityAccess'
import { type AccountActivityItem, buildAccountActivity } from './accountActivity'

type UseAccountActivityProps = {
  workflow: Workflow
  account: AccountData
  canManageAccount: boolean
  canEditAccount: boolean
}

type AccountActivity = {
  activity: AccountActivityItem[]
  access: ActivityAccess
}

export const useAccountActivity = ({
  workflow,
  account,
  canManageAccount,
  canEditAccount,
}: UseAccountActivityProps): AccountActivity => {
  const { user } = useProfile()
  return useMemo(() => {
    if (isNotDefined(user)) return { activity: [], access: 'none' }
    const access = getActivityAccess({
      canSeeHistory: workflow.canSeeHistory(user),
      canManageAccount,
      canEditAccount,
      isCurrentOwner: account.ownerId === user.userId,
    })
    const visibleWindows = toVisibleWindows({ access, account, viewerId: user.userId })
    const activityAccount = toLegacyActivityAccount({ account, access, viewerId: user.userId })
    return { activity: buildAccountActivity({ workflow, account: activityAccount, visibleWindows }), access }
  }, [workflow, account, user, canManageAccount, canEditAccount])
}

type ToVisibleWindowsProps = {
  access: ActivityAccess
  account: AccountData
  viewerId: UserId
}

// No windows hides everything; undefined shows everything
const toVisibleWindows = ({ access, account, viewerId }: ToVisibleWindowsProps): TimeWindow[] | undefined => {
  if (access === 'none') return []
  if (access === 'all' || isEmpty(account.ownerHistory)) return
  return getOwnershipWindows({ ownerHistory: account.ownerHistory, userId: viewerId })
}
